import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  MeasuringStrategy,
  type CollisionDetection,
} from "@dnd-kit/core";
import {
  Image as ImageIcon,
  Loader2,
  RefreshCcw,
  Check,
  LayoutGrid,
  Columns3,
} from "lucide-react";
import clsx from "clsx";
import { api, apiUrl } from "../api";
import {
  Button,
  classificationLabel,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHeader,
  Select,
  useToast,
} from "../ui";

// ---------------------------------------------------------------------------
// Bild-Bibliothek (Task d6f8ec59) — der Mitarbeiter SIEHT die extrahierten
// Figur-Crops (Thumbnail-Grid), kuratiert je Figur eine Disposition + ein
// Mapping (Claim / Pillar / Section-Rolle), filterbar nach Klassifikation /
// Disposition / Figur-Typ. Persistenz via PATCH /api/blog/assets/{id}.
// ---------------------------------------------------------------------------

interface ImageAsset {
  id: string;
  source_type: string;
  source_ref: string;
  page_number: number | null;
  title: string | null;
  classification: string | null;
  figure_type: string | null;
  context_text: string | null;
  description_content: string | null;
  claim_id: string | null;
  disposition: string | null;
  mapped_pillar: string | null;
  mapped_section_role: string | null;
  curated_at: string | null;
  curated_by: string | null;
  width: number | null;
  height: number | null;
  mime: string | null;
  status: string;
  thumb_url: string;
  // Regeneration (Task 6bdbe416, T-05): before/after in der Bibliothek.
  regenerated_thumb_url: string | null;
  regenerated_at: string | null;
}

interface ClaimSummary {
  id: string;
  claim_key: string;
  claim_label: string;
  pillar: string;
}

// --- Disposition: die 4 vom Task vorgegebenen Werte + deutsche Labels ---
const DISPOSITIONS: { value: string; label: string }[] = [
  { value: "reuse_1to1", label: "1:1 wiederverwenden" },
  { value: "regenerate", label: "Neu generieren" },
  { value: "mechanism", label: "Mechanismus-Grafik" },
  { value: "discard", label: "Verwerfen" },
];
const DISPOSITION_LABEL: Record<string, string> = Object.fromEntries(
  DISPOSITIONS.map((d) => [d.value, d.label]),
);

// --- Klassifikation (aus 0064/0065): Anzeige-Labels + Filter-Optionen ---
const CLASSIFICATIONS = [
  "reuse_1to1",
  "study_graphic",
  "inspiration_editorial",
  "inspiration_infographic",
  "decorative_skipped",
  "unclassified",
];

// --- Figur-Typen (Migration 0065) ---
const FIGURE_TYPES = ["study_chart", "mechanism_diagram", "photo", "decorative"];

// --- Die 6 bekannten Pillars (app/services/blog_archetypes.py) ---
const PILLARS = [
  "active_beauty",
  "biotechnology",
  "regulatory_safety",
  "trends_consumer",
  "practical_formulation",
  "sustainability_innovation",
];

// --- SectionRole-Werte (app/services/blog_archetypes.py::SectionRole) ---
const SECTION_ROLES = [
  "application",
  "brief",
  "clinical_translation",
  "comparison_table",
  "constraints",
  "decision_framework",
  "direct_answer",
  "evidence",
  "evidence_summary",
  "faq",
  "market_signal",
  "mechanism",
  "molecular_level",
  "next_step",
  "outlook",
  "practical_implication",
  "proof",
  "question",
  "rahmenrezeptur_hint",
  "regulatory_status",
  "requirement",
  "solution_architecture",
  "status",
  "story",
  "supplier_origin",
  "tl_dr",
  "when_a",
  "when_b",
  "why",
];

const DISPOSITION_BADGE: Record<string, string> = {
  reuse_1to1: "bg-success/15 text-success",
  regenerate: "bg-accent-500/15 text-accent-600",
  mechanism: "bg-info/15 text-info",
  discard: "bg-danger/15 text-danger",
};

// ---------------------------------------------------------------------------
// Kanban-Ansicht (Task): eine Spalte je Disposition (inkl. "unkuratiert" =
// disposition null). Drag&Drop zwischen den Spalten setzt die Disposition per
// PATCH — optimistic + Rollback. DnD-Mechanik gespiegelt von pages/Kanban.tsx
// (loest den 0x0-Droppable-Gotcha: EIN sichtbares horizontal-scrollbares
// Layout, MeasuringStrategy.Always, real gerenderte min-h Drop-Flaeche).
// ---------------------------------------------------------------------------

// disposition-Wert je Spalte; null = unkuratiert.
const KANBAN_COLUMNS: { value: string | null; label: string }[] = [
  { value: null, label: "Unkuratiert" },
  ...DISPOSITIONS,
];

// Droppable-IDs muessen Strings sein → null wird als "__none__" kodiert.
const NONE_COL = "__none__";
const colId = (v: string | null) => v ?? NONE_COL;
const colValue = (id: string): string | null => (id === NONE_COL ? null : id);

const VIEW_KEY = "sb_imglib_view";
type LibView = "grid" | "kanban";
function readView(): LibView {
  if (typeof window === "undefined") return "grid";
  return window.localStorage.getItem(VIEW_KEY) === "kanban" ? "kanban" : "grid";
}

function Badge({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={clsx(
        "inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-md",
        className ?? "bg-surface-3 text-text-2",
      )}
    >
      {children}
    </span>
  );
}

export default function ImageLibraryPage() {
  const toast = useToast();
  const [assets, setAssets] = useState<ImageAsset[]>([]);
  const [claims, setClaims] = useState<ClaimSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [classFilter, setClassFilter] = useState<string>("");
  const [dispFilter, setDispFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  // Regeneriert-Filter (Task fa4d6621): "" = Alle, "true" = nur regenerierte,
  // "false" = nur nicht-regenerierte. Erreicht das Backend als has_regenerated.
  const [regenFilter, setRegenFilter] = useState<string>("");
  const [selected, setSelected] = useState<ImageAsset | null>(null);
  // View-Toggle Grid <-> Kanban (persistiert, Default grid). Kanban ist der
  // alternative View; Grid + CurateModal bleiben unveraendert erhalten.
  const [view, setView] = useState<LibView>(readView);
  const changeView = useCallback((v: LibView) => {
    setView(v);
    try {
      window.localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* localStorage nicht verfuegbar — View bleibt in-memory */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (classFilter) params.set("classification", classFilter);
      if (dispFilter) params.set("disposition", dispFilter);
      if (typeFilter) params.set("figure_type", typeFilter);
      if (regenFilter) params.set("has_regenerated", regenFilter);
      const q = params.toString();
      const rows = await api.get<ImageAsset[]>(
        `/api/blog/assets${q ? `?${q}` : ""}`,
      );
      setAssets(rows);
    } catch (e) {
      toast.err("Bild-Bibliothek konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [classFilter, dispFilter, typeFilter, regenFilter, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Approved Claims fuer den Claim-Dropdown im Kurations-Modal (einmalig).
  useEffect(() => {
    api
      .get<ClaimSummary[]>("/api/claims?status_filter=approved&limit=500")
      .then(setClaims)
      .catch(() => setClaims([]));
  }, []);

  const anyFilter = classFilter || dispFilter || typeFilter || regenFilter;

  const onCurated = (updated: ImageAsset) => {
    // Row im Grid ersetzen; wenn der aktive Disposition-Filter sie jetzt
    // ausschliesst, aus der Liste nehmen.
    setAssets((prev) =>
      prev
        .map((a) => (a.id === updated.id ? updated : a))
        .filter((a) => !dispFilter || a.disposition === dispFilter),
    );
    setSelected(null);
  };

  // Disposition per Drag&Drop setzen (Kanban). OPTIMISTIC: das Asset lokal
  // sofort in die Zielspalte verschieben, dann PATCH. Bei Fehler ROLLBACK auf
  // die alte disposition (Karte rollt sichtbar zurueck, kein stiller Verlust).
  const moveDisposition = useCallback(
    async (id: string, next: string | null) => {
      const before = assets.find((a) => a.id === id);
      if (!before || before.disposition === next) return;
      // 1) optimistic: sofort umbucketen.
      setAssets((prev) =>
        prev.map((a) => (a.id === id ? { ...a, disposition: next } : a)),
      );
      try {
        const updated = await api.patch<ImageAsset>(
          `/api/blog/assets/${id}`,
          { disposition: next },
        );
        // 2) Server-Wahrheit uebernehmen (curated_at/by etc.).
        setAssets((prev) => prev.map((a) => (a.id === id ? updated : a)));
      } catch (e) {
        // 3) Rollback: alte disposition wiederherstellen.
        setAssets((prev) =>
          prev.map((a) =>
            a.id === id ? { ...a, disposition: before.disposition } : a,
          ),
        );
        toast.err("Disposition konnte nicht gespeichert werden — zurückgesetzt.");
      }
    },
    [assets, toast],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Bild-Bibliothek"
        description="Extrahierte Figuren aus dem Factsheet-PDF — sichten + kuratieren (Disposition & Mapping)."
        actions={
          <div className="flex items-center gap-2">
            {/* View-Umschalter Grid <-> Kanban (Segmented-Control) */}
            <div className="inline-flex rounded-lg border border-border bg-surface p-0.5">
              <button
                type="button"
                onClick={() => changeView("grid")}
                aria-pressed={view === "grid"}
                className={clsx(
                  "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition",
                  view === "grid"
                    ? "bg-accent-500/15 text-accent-600"
                    : "text-text-2 hover:text-text",
                )}
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                Grid
              </button>
              <button
                type="button"
                onClick={() => changeView("kanban")}
                aria-pressed={view === "kanban"}
                className={clsx(
                  "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition",
                  view === "kanban"
                    ? "bg-accent-500/15 text-accent-600"
                    : "text-text-2 hover:text-text",
                )}
              >
                <Columns3 className="w-3.5 h-3.5" />
                Kanban
              </button>
            </div>
            <Button variant="ghost" onClick={load} disabled={loading}>
              <RefreshCcw className={clsx("w-4 h-4", loading && "animate-spin")} />
              Neu laden
            </Button>
          </div>
        }
      />

      {/* Filter-Leiste — nur im Grid-View (im Kanban sind die Spalten der
          Disposition-Schnitt; die Grid-Filter waeren dort redundant). */}
      {view === "grid" && (
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Klassifikation" className="w-52">
          <Select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
            <option value="">Alle</option>
            {CLASSIFICATIONS.map((c) => (
              <option key={c} value={c}>
                {classificationLabel(c)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Disposition" className="w-52">
          <Select value={dispFilter} onChange={(e) => setDispFilter(e.target.value)}>
            <option value="">Alle</option>
            <option value="__none__" disabled>
              — kuratiert —
            </option>
            {DISPOSITIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Figur-Typ" className="w-52">
          <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">Alle</option>
            {FIGURE_TYPES.map((t) => (
              <option key={t} value={t}>
                {classificationLabel(t)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Regeneriert" className="w-40">
          <Select value={regenFilter} onChange={(e) => setRegenFilter(e.target.value)}>
            <option value="">Alle</option>
            <option value="true">Ja</option>
            <option value="false">Nein</option>
          </Select>
        </Field>
        {anyFilter && (
          <Button
            variant="ghost"
            onClick={() => {
              setClassFilter("");
              setDispFilter("");
              setTypeFilter("");
              setRegenFilter("");
            }}
          >
            Alle anzeigen
          </Button>
        )}
        <span className="text-xs text-text-3 ml-auto self-center">
          {assets.length} Figur{assets.length === 1 ? "" : "en"}
        </span>
      </div>
      )}

      {view === "kanban" ? (
        <DispositionKanban
          assets={assets}
          loading={loading}
          onOpen={setSelected}
          onMove={moveDisposition}
        />
      ) : loading && assets.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-text-2 py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Lade Figuren…
        </div>
      ) : assets.length === 0 ? (
        <EmptyState
          icon={<ImageIcon className="w-8 h-8" />}
          title="Keine Figuren"
          hint={
            regenFilter === "true"
              ? "Keine regenerierten Figuren."
              : anyFilter
                ? "Keine Figur passt zu den aktiven Filtern."
                : "Es sind noch keine Figuren extrahiert."
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {assets.map((a) => (
            <button
              key={a.id}
              onClick={() => setSelected(a)}
              className="text-left border border-border rounded-lg bg-surface overflow-hidden hover:border-accent-500 transition focus:outline-none focus:ring-1 focus:ring-accent-500"
            >
              {a.regenerated_thumb_url ? (
                // Before/After (Task fa4d6621): Original + regenerierte Version
                // nebeneinander, gleiche Kachelgroesse, aligned, kein Layout-Sprung.
                <div className="grid grid-cols-2 divide-x divide-border">
                  <figure className="space-y-0">
                    <div className="aspect-video bg-surface-2 overflow-hidden flex items-center justify-center text-text-3">
                      <img
                        src={apiUrl(a.thumb_url)}
                        alt="Original"
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                    <figcaption className="text-[10px] text-text-3 text-center py-1 bg-surface-2/60">
                      Original
                    </figcaption>
                  </figure>
                  <figure className="space-y-0">
                    <div className="aspect-video bg-surface-2 overflow-hidden flex items-center justify-center text-text-3">
                      <img
                        src={apiUrl(a.regenerated_thumb_url)}
                        alt="Regeneriert"
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                    <figcaption className="text-[10px] text-text-3 text-center py-1 bg-surface-2/60">
                      Regeneriert
                    </figcaption>
                  </figure>
                </div>
              ) : (
                <div className="aspect-video bg-surface-2 overflow-hidden flex items-center justify-center text-text-3">
                  <img
                    src={apiUrl(a.thumb_url)}
                    alt={a.context_text ?? a.figure_type ?? "Figur"}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
              )}
              <div className="p-3 space-y-2">
                {a.title ? (
                  <h3
                    className="text-sm font-medium text-text truncate"
                    title={a.title}
                  >
                    {a.title}
                  </h3>
                ) : (
                  <h3 className="text-sm italic text-text-3 truncate">
                    Ohne Titel
                  </h3>
                )}
                <div className="flex flex-wrap items-center gap-1">
                  {a.figure_type && <Badge>{classificationLabel(a.figure_type)}</Badge>}
                  {a.classification && (
                    <Badge className="bg-surface-3 text-text-2">{classificationLabel(a.classification)}</Badge>
                  )}
                  {a.disposition && (
                    <Badge className={DISPOSITION_BADGE[a.disposition]}>
                      {DISPOSITION_LABEL[a.disposition] ?? a.disposition}
                    </Badge>
                  )}
                </div>
                <p
                  className="text-xs text-text-2 line-clamp-2"
                  title={a.context_text ?? undefined}
                >
                  {a.context_text || (
                    <span className="italic text-text-3">Kein Kontext-Text</span>
                  )}
                </p>
                <div className="text-[10px] text-text-3 flex items-center gap-2">
                  <span className="truncate">{a.source_ref}</span>
                  {a.page_number != null && <span>· S. {a.page_number}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <CurateModal
          asset={selected}
          claims={claims}
          onClose={() => setSelected(null)}
          onCurated={onCurated}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DispositionKanban — eine Spalte je Disposition (inkl. Unkuratiert=null).
// Drag&Drop einer Karte in eine andere Spalte setzt deren Disposition (onMove,
// optimistic+rollback im Parent). DnD-Mechanik gespiegelt von pages/Kanban.tsx:
// - PointerSensor 6px (Klick=Modal bleibt Klick), TouchSensor 150ms/8px
//   (schnelles Wischen bleibt Scroll, erst Halten startet den Drag).
// - collisionDetection: pointerWithin, Fallback rectIntersection (Droppable
//   unter dem Cursor gewinnt).
// - measuring droppable = Always + EIN sichtbares, horizontal scrollbares
//   Layout (kein zweites verstecktes DOM) → keine 0x0-Droppable-Rects.
// - Jede Spalte ist ein useDroppable mit real gerenderter min-h Drop-Flaeche +
//   isOver-Highlight. DragOverlay rendert die gezogene Karte schwebend.
// ---------------------------------------------------------------------------

function DispositionKanban({
  assets,
  loading,
  onOpen,
  onMove,
}: {
  assets: ImageAsset[];
  loading: boolean;
  onOpen: (a: ImageAsset) => void;
  onMove: (id: string, next: string | null) => void;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
  );

  const collisionDetection = useCallback<CollisionDetection>((args) => {
    const within = pointerWithin(args);
    return within.length > 0 ? within : rectIntersection(args);
  }, []);

  // Assets nach disposition in Spalten bucketen (null → Unkuratiert-Spalte).
  const buckets = useMemo(() => {
    const map: Record<string, ImageAsset[]> = {};
    for (const col of KANBAN_COLUMNS) map[colId(col.value)] = [];
    for (const a of assets) {
      const key = a.disposition && map[a.disposition] ? a.disposition : NONE_COL;
      map[key].push(a);
    }
    return map;
  }, [assets]);

  const activeAsset = activeId
    ? assets.find((a) => a.id === activeId) ?? null
    : null;

  const handleDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const asset = assets.find((a) => a.id === String(active.id));
    if (!asset) return;
    const next = colValue(String(over.id));
    if (asset.disposition === next) return;
    onMove(asset.id, next);
  };

  if (loading && assets.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-text-2 py-12 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Lade Figuren…
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      {/* EIN Layout: horizontal scrollbare Spalten (Mobile nutzbar per
          overflow-x-auto) — kein zweites verstecktes DOM → keine 0x0-Rects. */}
      <div className="flex gap-3 overflow-x-auto pb-2">
        {KANBAN_COLUMNS.map((col) => (
          <KanbanColumn
            key={colId(col.value)}
            column={col}
            cards={buckets[colId(col.value)] ?? []}
            onOpen={onOpen}
          />
        ))}
      </div>
      <DragOverlay dropAnimation={null}>
        {activeAsset ? (
          <div style={{ width: 240 }}>
            <KanbanCardView asset={activeAsset} overlay />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function KanbanColumn({
  column,
  cards,
  onOpen,
}: {
  column: { value: string | null; label: string };
  cards: ImageAsset[];
  onOpen: (a: ImageAsset) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: colId(column.value) });
  return (
    <div className="flex-1 min-w-[220px] max-w-[300px] flex flex-col">
      {/* Spalten-Header: Label + Count */}
      <div className="flex items-center justify-between px-1 pb-2">
        <div className="flex items-center gap-1.5">
          {column.value ? (
            <Badge className={DISPOSITION_BADGE[column.value]}>{column.label}</Badge>
          ) : (
            <Badge className="bg-surface-3 text-text-2">{column.label}</Badge>
          )}
        </div>
        <span className="text-xs text-text-3 tabular-nums">{cards.length}</span>
      </div>
      {/* Real gerenderte Drop-Flaeche mit min-h + isOver-Highlight. */}
      <div
        ref={setNodeRef}
        className={clsx(
          "flex-1 rounded-lg border p-2 space-y-2 transition min-h-[160px]",
          isOver
            ? "border-accent-500 bg-accent-500/5 outline outline-1 outline-accent-500/40"
            : "border-border bg-surface-2/40",
        )}
      >
        {cards.length === 0 ? (
          <div className="h-[120px] flex items-center justify-center text-xs text-text-3 italic select-none">
            Leer
          </div>
        ) : (
          cards.map((a) => (
            <KanbanCard key={a.id} asset={a} onOpen={onOpen} />
          ))
        )}
      </div>
    </div>
  );
}

function KanbanCard({
  asset,
  onOpen,
}: {
  asset: ImageAsset;
  onOpen: (a: ImageAsset) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: asset.id,
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      // Klick oeffnet weiterhin das CurateModal (Feindetails, non_goals). Der
      // 6px-PointerSensor trennt Klick (Modal) von Drag (Disposition).
      onClick={() => onOpen(asset)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(asset);
        }
      }}
      className={clsx(
        "cursor-grab active:cursor-grabbing touch-none",
        isDragging && "opacity-40",
      )}
    >
      <KanbanCardView asset={asset} />
    </div>
  );
}

// Reine Darstellung einer Kanban-Karte (auch im DragOverlay verwendet):
// kompaktes Thumbnail + Titel.
function KanbanCardView({
  asset,
  overlay,
}: {
  asset: ImageAsset;
  overlay?: boolean;
}) {
  return (
    <div
      className={clsx(
        "border rounded-lg bg-surface overflow-hidden",
        overlay
          ? "border-accent-500 shadow-lg"
          : "border-border hover:border-accent-500 transition",
      )}
    >
      <div className="aspect-video bg-surface-2 overflow-hidden flex items-center justify-center text-text-3">
        <img
          src={apiUrl(asset.thumb_url)}
          alt={asset.title ?? asset.figure_type ?? "Figur"}
          className="w-full h-full object-contain pointer-events-none"
          loading="lazy"
        />
      </div>
      <div className="px-2 py-1.5">
        {asset.title ? (
          <p className="text-xs font-medium text-text truncate" title={asset.title}>
            {asset.title}
          </p>
        ) : (
          <p className="text-xs italic text-text-3 truncate">Ohne Titel</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kurations-Modal (zentriert, ui/modal.tsx). Grosses Crop + voller Kontext +
// Controls fuer Disposition + Claim/Pillar/Section-Mapping.
// ---------------------------------------------------------------------------

function CurateModal({
  asset,
  claims,
  onClose,
  onCurated,
}: {
  asset: ImageAsset;
  claims: ClaimSummary[];
  onClose: () => void;
  onCurated: (a: ImageAsset) => void;
}) {
  const toast = useToast();
  const [title, setTitle] = useState(asset.title ?? "");
  const [disposition, setDisposition] = useState(asset.disposition ?? "");
  const [claimId, setClaimId] = useState(asset.claim_id ?? "");
  const [pillar, setPillar] = useState(asset.mapped_pillar ?? "");
  const [sectionRole, setSectionRole] = useState(asset.mapped_section_role ?? "");
  const [saving, setSaving] = useState(false);
  // Regeneration (T-05): before/after. regenUrl reflektiert die akzeptierte
  // regenerierte Version (aus der API-Antwort), regenTs erzwingt einen frischen
  // <img>-Load nach erfolgreicher Regeneration (Cache-Bust).
  const [regenerating, setRegenerating] = useState(false);
  const [regenUrl, setRegenUrl] = useState(asset.regenerated_thumb_url);
  const [regenTs, setRegenTs] = useState(0);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const updated = await api.post<ImageAsset>(
        `/api/blog/assets/${asset.id}/regenerate`,
      );
      setRegenUrl(updated.regenerated_thumb_url);
      setRegenTs(Date.now());
      onCurated(updated);
      toast.ok("Figur regeneriert.");
    } catch (e) {
      toast.err(
        "Regeneration fehlgeschlagen (kein datentreues, brand-sauberes Bild — kein Fake gespeichert).",
      );
    } finally {
      setRegenerating(false);
    }
  };

  const claimsForPillar = useMemo(() => {
    if (!pillar) return claims;
    const scoped = claims.filter((c) => c.pillar === pillar);
    // Falls kein Claim zum Pillar passt, alle zeigen (kein Sackgassen-Dropdown).
    return scoped.length ? scoped : claims;
  }, [claims, pillar]);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.patch<ImageAsset>(
        `/api/blog/assets/${asset.id}`,
        {
          title: title.trim() || null,
          disposition: disposition || null,
          claim_id: claimId || null,
          mapped_pillar: pillar || null,
          mapped_section_role: sectionRole || null,
        },
      );
      toast.ok("Kuratierung gespeichert.");
      onCurated(updated);
    } catch (e) {
      toast.err("Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="Figur kuratieren"
      description={`${asset.source_ref}${
        asset.page_number != null ? ` · Seite ${asset.page_number}` : ""
      }`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            Speichern
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Crop + Kontext */}
        <div className="space-y-3">
          <div className="rounded-lg border border-border bg-surface-2 overflow-hidden flex items-center justify-center">
            <img
              src={apiUrl(asset.thumb_url)}
              alt={asset.context_text ?? "Figur"}
              className="w-full max-h-[42vh] object-contain"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {asset.figure_type && <Badge>{classificationLabel(asset.figure_type)}</Badge>}
            {asset.classification && (
              <Badge className="bg-surface-3 text-text-2">{classificationLabel(asset.classification)}</Badge>
            )}
          </div>
          {asset.context_text && (
            <div>
              <div className="text-xs font-medium text-text mb-1">Kontext</div>
              <p className="text-xs text-text-2 whitespace-pre-wrap">
                {asset.context_text}
              </p>
            </div>
          )}
          {asset.description_content && (
            <div>
              <div className="text-xs font-medium text-text mb-1">Beschreibung</div>
              <p className="text-xs text-text-2 whitespace-pre-wrap">
                {asset.description_content}
              </p>
            </div>
          )}

          {/* Regeneration (T-05): nur bei disposition='regenerate'. Button
              erzeugt eine datentreue, brand-saubere Version; before/after wird
              nebeneinander gezeigt (Reviewer-Visual-Audit). */}
          {disposition === "regenerate" && (
            <div className="pt-2 border-t border-border space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-text">
                  Datentreue Regeneration
                </div>
                <Button
                  variant="ghost"
                  onClick={regenerate}
                  disabled={regenerating}
                  title="Figur aus Original + Kontext + Brand-Regeln neu zeichnen"
                >
                  {regenerating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCcw className="w-4 h-4" />
                  )}
                  Regenerieren
                </Button>
              </div>
              {regenUrl ? (
                <div className="grid grid-cols-2 gap-2">
                  <figure className="space-y-1">
                    <img
                      src={apiUrl(asset.thumb_url)}
                      alt="Original"
                      className="w-full rounded-lg border border-border bg-surface-2 object-contain max-h-[26vh]"
                    />
                    <figcaption className="text-[11px] text-text-3 text-center">
                      Original
                    </figcaption>
                  </figure>
                  <figure className="space-y-1">
                    <img
                      src={`${apiUrl(regenUrl)}${regenTs ? `?t=${regenTs}` : ""}`}
                      alt="Regeneriert"
                      className="w-full rounded-lg border border-accent-500/40 bg-surface-2 object-contain max-h-[26vh]"
                    />
                    <figcaption className="text-[11px] text-text-3 text-center">
                      Regeneriert
                    </figcaption>
                  </figure>
                </div>
              ) : (
                <p className="text-[11px] text-text-3">
                  Noch keine regenerierte Version. „Regenerieren" erzeugt eine
                  saubere, datentreue Fassung (kein Fake bei Fehlschlag).
                </p>
              )}
            </div>
          )}
        </div>

        {/* Kurations-Controls */}
        <div className="space-y-4">
          <Field label="Titel" hint="Menschenlesbar — für die Bibliothek.">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z. B. SELS Roughness Study"
              maxLength={120}
            />
          </Field>

          <Field label="Disposition">
            <Select
              value={disposition}
              onChange={(e) => setDisposition(e.target.value)}
            >
              <option value="">— nicht gesetzt —</option>
              {DISPOSITIONS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Pillar" optional>
            <Select value={pillar} onChange={(e) => setPillar(e.target.value)}>
              <option value="">— nicht gesetzt —</option>
              {PILLARS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Claim"
            optional
            hint={
              claims.length === 0
                ? "Keine freigegebenen Claims verfügbar."
                : undefined
            }
          >
            <Select value={claimId} onChange={(e) => setClaimId(e.target.value)}>
              <option value="">— nicht gesetzt —</option>
              {claimsForPillar.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.claim_label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Section-Rolle" optional>
            <Select
              value={sectionRole}
              onChange={(e) => setSectionRole(e.target.value)}
            >
              <option value="">— nicht gesetzt —</option>
              {SECTION_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          </Field>

          {asset.curated_at && (
            <p className="text-[11px] text-text-3">
              Zuletzt kuratiert: {new Date(asset.curated_at).toLocaleString("de-DE")}
              {asset.curated_by ? ` · ${asset.curated_by}` : ""}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
